import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from "react";
import { paletteColor } from "../lib/palette";
import { useSearchParams } from "react-router-dom";
import {
  UserRound,
  ArchiveRestore,
  Archive,
  Check,
  CheckCheck,
  ChevronDown,
  Clock,
  Download,
  Instagram,
  Mail,
  MailOpen,
  FileText,
  Headphones,
  Image as ImageIcon,
  Mic,
  MessageCircle,
  MessagesSquare,
  Paperclip,
  PenLine,
  Pin,
  Plus,
  Reply,
  Search,
  Send,
  Star,
  Trash2,
  User,
  X,
} from "lucide-react";
import { useStore } from "../lib/store";
import { showFeedback } from "../lib/feedback";
import {
  CHAT_ATTACHMENT_MAX_BYTES,
  CHAT_ATTACHMENT_MAX_COUNT,
} from "../features/communication/commands";
import { contentHash } from "../features/communication/threading";
import type {
  CommunicationChannel,
  CommunicationMessage,
  CommunicationThread,
  MessageAttachmentMeta,
} from "../features/communication/contracts";
import {
  CHAT_VIEW_LABEL,
  CHAT_VIEWS,
  chatChannelCounts,
  filterChatThreads,
  groupMessagesByDay,
  isChatView,
  lastMessageOf,
  listStamp,
  unreadCountOf,
  shortTime,
  threadMessages,
  type ChatView,
} from "../features/communication/inbox-view";
import { HEAT_EXPLANATION, leadHeat, priorityLabel } from "../features/leads/list";
import { PageHead } from "../ui/PageHead";
import { SideNav } from "../ui/SideNav";
import { Tabs } from "../ui/Tabs";
import { Button } from "../ui/Button";
import { IconButton } from "../ui/IconButton";
import { Menu } from "../ui/Menu";
import { SearchInput } from "../ui/SearchInput";
import { EmptyState } from "../ui/EmptyState";
import { Avatar } from "../ui/Avatar";
import { Dialog } from "../ui/Dialog";
import { HelpPopover } from "../ui/HelpPopover";
import { Select } from "../ui/Select";
import { Field } from "../ui/Field";
import { NewTaskDialog } from "../features/tasks/NewTaskDialog";
import { InboxPage } from "./InboxPage";
import { money } from "./lista-popovers";
import { LeadQuickPeek } from "./ListaPage";
import { useProfileIdentity } from "../app/useProfileIdentity";
import { CosmosAiIcon } from "../ui/CosmosAiIcon";
import { improveDraft, localSuggestion } from "../features/communication/local-suggestion";
import "./mensagens.css";

/**
 * Mensagens — conversas de WhatsApp, Instagram e Messenger.
 *
 * O protótipo é explícito: os quatro canais são demonstração local, sem
 * conexão real. Aqui isso não vira controle desabilitado: histórico, rascunho,
 * modelos e envio funcionam de verdade sobre o domínio de comunicação, e a
 * tentativa nasce `simulated` — nada sai deste ambiente (§27.3). O e-mail tem
 * superfície própria (`?canal=email`), porque tem cadeia própria de envio.
 */

const CHAT_CHANNEL_LABEL: Record<"whatsapp" | "instagram" | "messenger", string> = {
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  messenger: "Messenger",
};

const CHANNEL_TONE: Record<CommunicationChannel, string> = {
  whatsapp: "var(--good)",
  instagram: "var(--cx-pink)",
  messenger: "var(--accent)",
  email: "var(--accent-2)",
};

function channelIcon(channel: CommunicationChannel) {
  if (channel === "whatsapp") return <MessageCircle aria-hidden="true" />;
  if (channel === "instagram") return <Instagram aria-hidden="true" />;
  if (channel === "messenger") return <MessagesSquare aria-hidden="true" />;
  return <Mail aria-hidden="true" />;
}

const DEMO_NOTE =
  "WhatsApp, Instagram, Messenger e e-mail sem conexão real. Histórico, rascunhos, sugestões e envios são demonstrações locais; nenhuma ação envia conteúdo externamente.";

/**
 * Categorias do menu de anexos, como no protótipo: cada uma abre o seletor do
 * sistema já filtrado e recusa o arquivo de outra categoria, para o nome do
 * item corresponder ao que ele realmente aceita (§18).
 */
type AttachmentKind = "visual" | "document" | "audio";

interface PendingAttachment {
  id: string;
  filename: string;
  declaredMimeType: string;
  size: number;
  contentHash: string;
}

const ATTACHMENT_ACCEPT: Record<AttachmentKind, string> = {
  visual: "image/*,video/*",
  audio: "audio/*",
  document: ".pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.zip,.rtf,.json",
};

function matchesKind(kind: AttachmentKind, mimeType: string): boolean {
  if (kind === "visual") return /^(image|video)\//.test(mimeType);
  if (kind === "audio") return mimeType.startsWith("audio/");
  return !/^(image|video|audio)\//.test(mimeType);
}

/** Tamanho legível ao lado do nome do arquivo. */
function fileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

type ChannelFilter = "all" | "whatsapp" | "instagram" | "messenger" | "trash";

const CHANNEL_FILTERS: ChannelFilter[] = ["all", "whatsapp", "instagram", "messenger", "trash"];

function isChannelFilter(value: string | null): value is ChannelFilter {
  return CHANNEL_FILTERS.includes((value ?? "") as ChannelFilter);
}

export function MensagensPage() {
  const [search] = useSearchParams();
  // O e-mail tem cadeia própria de envio e pastas: superfície separada, mesmo
  // endereço, como o protótipo faz com o par Mensagens↔Inbox.
  if (search.get("canal") === "email") return <InboxPage />;
  return <ChatSurface />;
}

function ChatSurface() {
  const store = useStore();
  /** Lead do acesso rápido aberto por "Ver contato"; o perfil é outra ação. */
  const [peekLeadId, setPeekLeadId] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();

  const threads = useMemo(() => store.communicationThreads ?? [], [store.communicationThreads]);
  const messages = useMemo(() => store.communicationMessages ?? [], [store.communicationMessages]);

  const channel: ChannelFilter = isChannelFilter(params.get("canal")) ? (params.get("canal") as ChannelFilter) : "all";
  const view: ChatView = isChatView(params.get("pasta")) ? (params.get("pasta") as ChatView) : "active";
  const query = params.get("busca") ?? "";

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "conversa") next.delete("conversa");
    setParams(next, { replace: true });
  };

  const counts = useMemo(() => chatChannelCounts(threads), [threads]);
  const rows = useMemo(
    () => filterChatThreads(threads, messages, { channel, view, query }),
    [threads, messages, channel, view, query],
  );

  const [contextOpen, setContextOpen] = useState(true);
  const selectedId = params.get("conversa");
  /**
   * A conversa aberta sem parâmetro na URL não pode trocar sozinha: responder
   * reordena a lista, e quem estava lendo passaria a ler outra conversa. O
   * último id resolvido fica lembrado enquanto ele existir no recorte atual.
   */
  const lembrado = useRef<string | null>(null);
  const selected =
    rows.find((thread) => thread.id === selectedId) ??
    rows.find((thread) => thread.id === lembrado.current) ??
    rows[0];
  useEffect(() => {
    lembrado.current = selected?.id ?? null;
  });

  const unread = counts.all > 0 ? threads.filter((t) => t.channel !== "email" && !t.trashedAt && t.unread).length : 0;

  return (
    <div className="cx-mensagens cx-commpage">
      <PageHead
        title="Mensagens"
        sub={
          <>
            <strong>
              {unread} {unread === 1 ? "conversa não lida" : "conversas não lidas"}
            </strong>
            . Continue de onde parou.
          </>
        }
        actions={
          <>
            <span className="cx-comm-demo">
              <i aria-hidden="true" />
              Demonstração local
              <HelpPopover
                about="os canais"
                label="Sobre os canais"
                align="end"
                items={[{ term: "Demonstração local", description: DEMO_NOTE }]}
              />
            </span>
            <NewConversationButton />
          </>
        }
      />

      <div className="cx-commpage-workspace">
        <SideNav
          className="cx-commpage-nav"
          label="Canais de mensagens"
          value={channel}
          onChange={(id) => setParam("canal", id === "all" ? null : id)}
          groups={[
            {
              id: "canais",
              items: [
                { id: "all", label: "Todos", count: counts.all, icon: <MessagesSquare aria-hidden="true" /> },
                {
                  id: "whatsapp",
                  label: "WhatsApp",
                  count: counts.whatsapp,
                  icon: <MessageCircle aria-hidden="true" />,
                },
                { id: "instagram", label: "Instagram", count: counts.instagram, icon: <Instagram aria-hidden="true" /> },
                {
                  id: "messenger",
                  label: "Messenger",
                  count: counts.messenger,
                  icon: <MessagesSquare aria-hidden="true" />,
                },
              ],
            },
            {
              id: "arquivo",
              items: [{ id: "trash", label: "Lixeira", count: counts.trash, icon: <Trash2 aria-hidden="true" /> }],
            },
          ]}
        />

        <div className={`cx-msg-shell cx-commpage-shell${selected && contextOpen ? "" : " context-hidden"}`}>
          <section className="cx-msg-list" aria-label="Conversas">
            <div className="cx-msg-list-top">
              <h2 className="cx-msg-list-title">
                {channel === "trash" ? "Lixeira" : channel === "all" ? "Conversas" : CHAT_CHANNEL_LABEL[channel]}
                <span className="cx-msg-count">{rows.length}</span>
              </h2>
              <SearchInput
                value={query}
                onChange={(value) => setParam("busca", value || null)}
                label="Buscar conversas"
                placeholder="Buscar conversa…"
              />
            </div>
            {channel !== "trash" && (
              <Tabs
                className="cx-msg-views"
                label="Situação das conversas"
                value={view}
                onChange={(id) => setParam("pasta", id === "active" ? null : id)}
                items={CHAT_VIEWS.map((id) => ({ id, label: CHAT_VIEW_LABEL[id] }))}
              />
            )}
            <div className="cx-msg-rows">
              {rows.length === 0 ? (
                <EmptyState
                  title={query ? "Nenhuma conversa com esse texto" : "Nenhuma conversa aqui"}
                  description={
                    query
                      ? "Ajuste a busca para ver as conversas deste canal."
                      : channel === "trash"
                        ? "Conversas descartadas aparecem aqui e podem ser restauradas."
                        : "Abra uma conversa nova a partir de um cadastro."
                  }
                />
              ) : (
                rows.map((thread) => (
                  <ThreadRow
                    key={thread.id}
                    thread={thread}
                    current={thread.id === selected?.id}
                    onOpen={() => {
                      const next = new URLSearchParams(params);
                      next.set("conversa", thread.id);
                      setParams(next, { replace: true });
                    }}
                  />
                ))
              )}
            </div>
          </section>

          {selected ? (
            <Conversation
              key={selected.id}
              thread={selected}
              contextOpen={contextOpen}
              onToggleContext={() => setContextOpen((open) => !open)}
            />
          ) : (
            <section className="cx-msg-reader is-empty">
              <EmptyState
                title="Escolha uma conversa"
                description="A conversa aparece aqui, com o histórico e o que você já escreveu."
              />
            </section>
          )}

          {selected && contextOpen && (
            <ContextPanel
              thread={selected}
              onClose={() => setContextOpen(false)}
              onOpenLead={(id) => setPeekLeadId(id)}
            />
          )}
        </div>
      </div>
      {peekLeadId && <LeadQuickPeek leadId={peekLeadId} onClose={() => setPeekLeadId(null)} />}
    </div>
  );

  function ThreadRow({
    thread,
    current,
    onOpen,
  }: {
    thread: CommunicationThread;
    current: boolean;
    onOpen: () => void;
  }) {
    const last = lastMessageOf(messages, thread.id);
    const unreadCount = unreadCountOf(messages, thread);
    const lead = store.leads.find((item) => item.id === thread.leadId);
    return (
      <button
        type="button"
        className={`cx-msg-thread${thread.unread ? " is-unread" : ""}`}
        aria-current={current ? "true" : undefined}
        onClick={onOpen}
      >
        <ChannelAvatar name={lead?.name ?? thread.subject} channel={thread.channel} />
        <span className="cx-msg-thread-copy">
          <span className="cx-msg-thread-line">
            <strong>{lead?.name ?? thread.subject}</strong>
            <time dateTime={thread.lastMessageAt}>{listStamp(thread.lastMessageAt)}</time>
            {unreadCount > 0 && (
              <span className="cx-msg-unread">
                {unreadCount}
                <span className="cx-sr-only">
                  {unreadCount === 1 ? "mensagem não lida" : "mensagens não lidas"}
                </span>
              </span>
            )}
          </span>
          <span className="cx-msg-snippet">{last?.bodyPreview ?? "Sem mensagens"}</span>
        </span>
      </button>
    );
  }
}

function ChannelAvatar({
  name,
  channel,
  size,
}: {
  name: string;
  channel: CommunicationChannel;
  size?: "default" | "lg";
}) {
  return (
    <span className="cx-msg-avatar" style={{ "--cx-msg-channel": CHANNEL_TONE[channel] } as CSSProperties}>
      <Avatar name={name} size={size} />
      <i aria-hidden="true">{channelIcon(channel)}</i>
    </span>
  );
}

/** Conversa aberta: histórico por dia e a barra de escrita do protótipo. */
/** Nome do autor da mensagem, como a citação e o menu de salvas mostram. */
function messageAuthor(
  direction: "inbound" | "outbound",
  identityName: string,
  counterpart: string,
): string {
  return direction === "outbound" ? identityName : counterpart;
}

/** Trecho legível de uma mensagem; excluída não devolve texto nenhum. */
function messageExcerpt(message: CommunicationMessage | undefined): string {
  if (!message) return "Mensagem indisponível";
  if (message.deletedAt) return "Mensagem excluída";
  return message.bodyText;
}

/**
 * O valor do cadastro não é receita: o Financeiro é quem conta dinheiro
 * recebido. O painel diz isso onde o número aparece (§10).
 */
const VALUE_EXPLANATION = [
  {
    term: "O que é",
    description:
      "Valor cadastral da negociação, informado no lead. Não representa receita recebida nem LTV.",
  },
  {
    term: "Onde muda",
    description: "Na edição do lead. Recebimentos e contratos vivem no Financeiro, não aqui.",
  },
];

const CAMINHO_PROIBIDO = '<>:"/\\|?*';

/** Nome de arquivo seguro para o download local da mensagem. */
function safeFileName(value: string): string {
  // Caracteres de controle saem por código, não por classe de regex: a
  // classe literal é proibida pelo lint e diz menos que a intenção.
  const cleaned = [...value]
    .map((char) => (char.charCodeAt(0) < 0x20 || CAMINHO_PROIBIDO.includes(char) ? "-" : char))
    .join("")
    .replace(/[. ]+$/, "")
    .slice(0, 120)
    .trim();
  return cleaned || "mensagem";
}

function Conversation({
  thread,
  contextOpen,
  onToggleContext,
}: {
  thread: CommunicationThread;
  contextOpen: boolean;
  onToggleContext: () => void;
}) {
  const store = useStore();
  const messages = useMemo(() => store.communicationMessages ?? [], [store.communicationMessages]);
  const lead = store.leads.find((item) => item.id === thread.leadId);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState<{ kind: "improve" | "suggest"; text: string } | null>(
    null,
  );
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [search, setSearch] = useState<string | null>(null);
  /** Citação em preparo: vive na tela, e só vira dado quando a resposta sai. */
  const [replyTo, setReplyTo] = useState<string | null>(null);
  /**
   * Anexos escolhidos e ainda não enviados.
   *
   * Ficam na tela porque o rascunho de chat não tem fila nem Storage: o que
   * vira dado são os metadados, no momento do envio (§27.3).
   */
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const pickKindRef = useRef<AttachmentKind>("document");
  const identity = useProfileIdentity();
  const scrollRef = useRef<HTMLDivElement>(null);
  const nodesRef = useRef(new Map<string, HTMLElement>());
  const items = useMemo(() => threadMessages(messages, thread.id), [messages, thread.id]);
  // Buscar dentro da conversa filtra o histórico visível; não apaga nada.
  const visible = useMemo(() => {
    const term = (search ?? "").trim().toLowerCase();
    if (!term) return items;
    return items.filter((message) => message.bodyText.toLowerCase().includes(term));
  }, [items, search]);
  const days = useMemo(() => groupMessagesByDay(visible), [visible]);
  const byId = useMemo(() => new Map(items.map((message) => [message.id, message])), [items]);
  // Os metadados do anexo vivem à parte da mensagem; o índice evita varrer a
  // lista inteira em cada bolha.
  const attachmentsByMessage = useMemo(() => {
    const index = new Map<string, MessageAttachmentMeta[]>();
    for (const file of store.messageAttachments ?? []) {
      const list = index.get(file.messageId);
      if (list) list.push(file);
      else index.set(file.messageId, [file]);
    }
    return index;
  }, [store.messageAttachments]);
  const pinned = useMemo(() => items.filter((m) => m.pinnedAt && !m.deletedAt), [items]);
  const starred = useMemo(() => items.filter((m) => m.starredAt && !m.deletedAt), [items]);
  const trashed = Boolean(thread.trashedAt);
  const archived = thread.state === "resolved";
  // Conversa fechada não recebe marca nem exclusão: o comando recusaria, e um
  // controle que só falha depois do clique é controle falso (§18).
  const lockedReason = trashed
    ? "Restaure a conversa da lixeira para usar esta ação."
    : archived
      ? "Reabra a conversa para usar esta ação."
      : undefined;
  const counterpart = lead?.name ?? thread.subject;

  // Ler a conversa marca como lida: é o que o protótipo faz ao abrir.
  useEffect(() => {
    if (!thread.unread || trashed) return;
    void store.transitionEmailThread({ threadId: thread.id, transition: "mark_read" });
    // A conversa é a dependência real; o comando não muda entre renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.id]);

  // Trocar de conversa não carrega a citação nem os anexos da anterior.
  useEffect(() => {
    setReplyTo(null);
    setPending([]);
  }, [thread.id]);

  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [items.length, thread.id]);

  /** Leva o histórico até a mensagem citada, fixada ou favorita. */
  const jumpTo = (messageId: string) => {
    const node = nodesRef.current.get(messageId);
    if (!node) return;
    node.scrollIntoView({ block: "center" });
    node.focus({ preventScroll: true });
  };

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const body = text.trim();
    // Só anexo já é mensagem; nem texto nem anexo, não é.
    if ((!body && pending.length === 0) || busy) return;
    setBusy(true);
    try {
      const result = await store.sendChannelMessage({
        threadId: thread.id,
        bodyText: body,
        replyToId: replyTo ?? undefined,
        attachments: pending.map(({ filename, declaredMimeType, size, contentHash }) => ({
          filename,
          declaredMimeType,
          size,
          contentHash,
        })),
      });
      if (result.ok) {
        setText("");
        setSuggestion(null);
        setReplyTo(null);
        setPending([]);
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * Arquivos escolhidos no menu de anexos.
   *
   * A categoria do menu é o filtro: escolher uma foto em "Documento" avisa em
   * vez de mandar assim mesmo, como no protótipo. Tamanho e quantidade são
   * conferidos aqui para o erro aparecer antes do envio, e de novo no comando,
   * que é quem de fato recusa (§5).
   */
  const addFiles = (kind: AttachmentKind, files: FileList | null) => {
    if (!files?.length) return;
    setPending((current) => {
      const next = [...current];
      for (const file of Array.from(files)) {
        if (!matchesKind(kind, file.type)) {
          showFeedback(`${file.name}: escolha a categoria correspondente no menu de anexos.`);
          continue;
        }
        if (file.size > CHAT_ATTACHMENT_MAX_BYTES) {
          showFeedback(`${file.name}: o limite local é de 10 MB por arquivo.`);
          continue;
        }
        if (next.length >= CHAT_ATTACHMENT_MAX_COUNT) {
          showFeedback("A mensagem comporta até 10 anexos.");
          break;
        }
        next.push({
          id: `${file.name}:${file.size}:${file.lastModified}`,
          filename: file.name,
          declaredMimeType: file.type || "application/octet-stream",
          size: file.size,
          // Sem Storage privado o conteúdo não entra no domínio; o hash
          // identifica o arquivo escolhido sem carregar seus bytes (§27.3).
          contentHash: contentHash([file.name, file.size, file.lastModified]),
        });
      }
      return next;
    });
  };

  const transition = async (kind: "resolve" | "reopen" | "trash" | "restore" | "mark_unread") => {
    await store.transitionEmailThread({ threadId: thread.id, transition: kind });
  };

  /**
   * Salvar a mensagem é um arquivo local, não um envio.
   *
   * A URL temporária é revogada logo depois de usada (§26.3).
   */
  const saveMessage = (message: CommunicationMessage) => {
    const author = messageAuthor(message.direction, identity.name, counterpart);
    const stamp = new Date(message.occurredAt).toLocaleString("pt-BR");
    const blob = new Blob([`${author} · ${stamp}\n\n${message.bodyText}\n`], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeFileName(`Mensagem - ${author}`)}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const runMessageAction = (message: CommunicationMessage, action: string) => {
    if (action === "save") return saveMessage(message);
    if (action === "reply") return setReplyTo(message.id);
    if (action === "pin")
      return void store.markChannelMessage({
        messageId: message.id,
        mark: "pinned",
        value: !message.pinnedAt,
      });
    if (action === "star")
      return void store.markChannelMessage({
        messageId: message.id,
        mark: "starred",
        value: !message.starredAt,
      });
    if (action === "delete") return void store.deleteChannelMessage({ messageId: message.id });
  };

  /** Menu de salvas: favoritas no cabeçalho, fixadas na faixa. */
  const savedSections = (list: CommunicationMessage[], empty: string) => [
    {
      id: "saved",
      items:
        list.length > 0
          ? list.map((message) => ({
              id: message.id,
              label: `${messageAuthor(message.direction, identity.name, counterpart)}: ${messageExcerpt(message)}`,
            }))
          : [{ id: "none", label: empty, disabled: true, disabledReason: empty }],
    },
  ];

  return (
    <section className="cx-msg-reader" aria-label={`Conversa com ${counterpart}`}>
      <header className="cx-msg-conv-head">
        <ChannelAvatar name={counterpart} channel={thread.channel} />
        <div className="cx-msg-person">
          <strong>{counterpart}</strong>
          <p>
            {lead?.company ?? thread.counterpartAddress}
            {lead && <> · <span className="cx-msg-channel">{channelName(thread.channel)}</span></>}
          </p>
        </div>
        <Menu
          label="Mensagens favoritas"
          align="end"
          className="cx-msg-saved-menu"
          sections={savedSections(starred, "Nenhuma mensagem favorita nesta conversa.")}
          onSelect={(id) => {
            if (id !== "none") jumpTo(id);
          }}
          trigger={(args) => (
            <IconButton
              {...args}
              label={`Mensagens favoritas (${starred.length})`}
              icon={<Star aria-hidden="true" />}
              variant="bare"
              className="cx-msg-favorites"
            />
          )}
        />
        <IconButton
          label={thread.unread ? "Marcar como lida" : "Marcar como não lida"}
          icon={thread.unread ? <MailOpen aria-hidden="true" /> : <Mail aria-hidden="true" />}
          variant="bare"
          onClick={() =>
            void store.transitionEmailThread({
              threadId: thread.id,
              transition: thread.unread ? "mark_read" : "mark_unread",
            })
          }
        />
        {thread.state === "resolved" ? (
          <IconButton
            label="Reabrir conversa"
            icon={<ArchiveRestore aria-hidden="true" />}
            variant="bare"
            onClick={() => void transition("reopen")}
          />
        ) : (
          <IconButton
            label="Arquivar conversa"
            icon={<Archive aria-hidden="true" />}
            variant="bare"
            disabled={trashed}
            onClick={() => void transition("resolve")}
          />
        )}
        {trashed ? (
          <IconButton
            label="Restaurar conversa"
            icon={<ArchiveRestore aria-hidden="true" />}
            variant="bare"
            onClick={() => void transition("restore")}
          />
        ) : (
          <IconButton
            label="Mover para a lixeira"
            icon={<Trash2 aria-hidden="true" />}
            variant="bare"
            onClick={() => void transition("trash")}
          />
        )}
        <IconButton
          label="Pesquisar nesta conversa"
          icon={<Search aria-hidden="true" />}
          variant="bare"
          active={search !== null}
          onClick={() => setSearch((current) => (current === null ? "" : null))}
        />
        <IconButton
          label={contextOpen ? "Ocultar detalhes do contato" : "Detalhes do contato"}
          icon={<UserRound aria-hidden="true" />}
          variant="bare"
          active={contextOpen}
          onClick={onToggleContext}
        />
      </header>

      {search !== null && (
        <div className="cx-msg-search">
          <SearchInput
            value={search}
            onChange={setSearch}
            label="Pesquisar nesta conversa"
            placeholder="Pesquisar nesta conversa…"
          />
          <span>
            {visible.length} de {items.length}
          </span>
        </div>
      )}

      {pinned.length > 0 && (
        <div className="cx-msg-saved-strip" aria-label="Mensagens fixadas">
          <button
            type="button"
            className="cx-msg-pinned-preview"
            title="Ir para a mensagem fixada"
            onClick={() => jumpTo(pinned[pinned.length - 1].id)}
          >
            <Pin aria-hidden="true" />
            <span>
              <strong>
                {messageAuthor(pinned[pinned.length - 1].direction, identity.name, counterpart)}
              </strong>
              {messageExcerpt(pinned[pinned.length - 1])}
            </span>
          </button>
          <Menu
            label="Mensagens fixadas"
            align="end"
            className="cx-msg-saved-menu"
            sections={savedSections(pinned, "Nenhuma mensagem fixada.")}
            onSelect={(id) => {
              if (id !== "none") jumpTo(id);
            }}
            trigger={(args) => (
              <button type="button" {...args} className="cx-msg-saved-trigger">
                Fixadas <b>{pinned.length}</b>
                <ChevronDown aria-hidden="true" />
              </button>
            )}
          />
        </div>
      )}

      <div className="cx-msg-reader-scroll" ref={scrollRef}>
        <div className="cx-msg-conversation">
          {days.length === 0 && (
            <EmptyState
              title={search ? "Nenhuma mensagem com esse texto" : "Conversa sem mensagens"}
              description={
                search ? "Ajuste a busca para ver o histórico." : "O que você escrever aparece aqui."
              }
            />
          )}
          {days.map((day) => (
            <div key={day.day} className="cx-msg-day-group">
              <div className="cx-msg-day">{day.label}</div>
              {day.messages.map((message, index) => {
                const previous = index > 0 ? day.messages[index - 1] : undefined;
                const grouped = previous?.direction === message.direction;
                const quoted = message.replyToId ? byId.get(message.replyToId) : undefined;
                return (
                  <article
                    key={message.id}
                    tabIndex={-1}
                    data-message-id={message.id}
                    ref={(node) => {
                      if (node) nodesRef.current.set(message.id, node);
                      else nodesRef.current.delete(message.id);
                    }}
                    className={[
                      "cx-msg-message",
                      message.direction === "outbound" ? "out" : "in",
                      grouped ? "is-grouped" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {grouped ? (
                      <span className="cx-msg-avatar-space" aria-hidden="true" />
                    ) : (
                      <ChannelAvatar
                        name={messageAuthor(message.direction, identity.name, counterpart)}
                        channel={thread.channel}
                      />
                    )}
                    <div className="cx-msg-message-content">
                      <div className="cx-msg-bubble">
                        {message.deletedAt ? (
                          <span className="cx-msg-deleted">
                            <Trash2 aria-hidden="true" />
                            Mensagem excluída
                          </span>
                        ) : (
                          <>
                            {message.replyToId && (
                              <div className="cx-msg-quote">
                                <button
                                  type="button"
                                  className="cx-msg-quote-body"
                                  disabled={!quoted}
                                  onClick={() => quoted && jumpTo(quoted.id)}
                                >
                                  <strong>
                                    {quoted
                                      ? messageAuthor(quoted.direction, identity.name, counterpart)
                                      : "Mensagem indisponível"}
                                  </strong>
                                  <span>{messageExcerpt(quoted)}</span>
                                </button>
                              </div>
                            )}
                            {message.bodyText}
                            {message.hasAttachments && (
                              <ul className="cx-msg-files" aria-label="Anexos da mensagem">
                                {(attachmentsByMessage.get(message.id) ?? []).map((file) => (
                                  <li key={file.id}>
                                    <Paperclip size={13} aria-hidden="true" />
                                    <span className="cx-msg-attach-name">{file.filename}</span>
                                    <span className="cx-msg-attach-size cx-data">
                                      {fileSize(file.size)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            {!trashed && (
                              <Menu
                                label="Ações da mensagem"
                                align="end"
                                className="cx-msg-menu"
                                sections={[
                                  {
                                    id: "conteudo",
                                    items: [
                                      { id: "save", label: "Salvar como", icon: <Download aria-hidden="true" /> },
                                      {
                                        id: "reply",
                                        label: "Responder mensagem",
                                        icon: <Reply aria-hidden="true" />,
                                        disabled: Boolean(lockedReason) || Boolean(message.deletedAt),
                                        disabledReason: lockedReason,
                                      },
                                    ],
                                  },
                                  {
                                    id: "marcas",
                                    items: [
                                      {
                                        id: "pin",
                                        label: message.pinnedAt ? "Desafixar mensagem" : "Fixar mensagem",
                                        icon: <Pin aria-hidden="true" />,
                                        disabled: Boolean(lockedReason),
                                        disabledReason: lockedReason,
                                      },
                                      {
                                        id: "star",
                                        label: message.starredAt
                                          ? "Remover dos favoritos"
                                          : "Favoritar mensagem",
                                        icon: <Star aria-hidden="true" />,
                                        disabled: Boolean(lockedReason),
                                        disabledReason: lockedReason,
                                      },
                                    ],
                                  },
                                  {
                                    id: "risco",
                                    items: [
                                      {
                                        id: "delete",
                                        label: "Excluir mensagem",
                                        icon: <Trash2 aria-hidden="true" />,
                                        danger: true,
                                        disabled: Boolean(lockedReason),
                                        disabledReason: lockedReason,
                                      },
                                    ],
                                  },
                                ]}
                                onSelect={(action) => runMessageAction(message, action)}
                                trigger={(args) => (
                                  <button
                                    type="button"
                                    {...args}
                                    className="cx-msg-more"
                                    title="Ações da mensagem"
                                    aria-label="Ações da mensagem"
                                  >
                                    <ChevronDown aria-hidden="true" />
                                  </button>
                                )}
                              />
                            )}
                          </>
                        )}
                      </div>
                      <footer className="cx-msg-message-meta">
                        {message.pinnedAt && !message.deletedAt && (
                          <span className="cx-msg-flag" title="Mensagem fixada" aria-label="Mensagem fixada">
                            <Pin aria-hidden="true" />
                          </span>
                        )}
                        {message.starredAt && !message.deletedAt && (
                          <span
                            className="cx-msg-flag is-star"
                            title="Mensagem favorita"
                            aria-label="Mensagem favorita"
                          >
                            <Star aria-hidden="true" />
                          </span>
                        )}
                        <time dateTime={message.occurredAt}>{shortTime(message.occurredAt)}</time>
                        {message.direction === "outbound" &&
                          (message.state === "delivered" ? (
                            <CheckCheck aria-hidden="true" />
                          ) : (
                            <Check aria-hidden="true" />
                          ))}
                      </footer>
                    </div>
                  </article>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {trashed ? (
        <div className="cx-msg-compose is-locked">
          Conversa na lixeira. Restaure para voltar a responder.
          <Button variant="ghost" onClick={() => void transition("restore")}>
            Restaurar
          </Button>
        </div>
      ) : thread.state === "resolved" ? (
        <div className="cx-msg-compose is-locked">
          Conversa arquivada. Reabra para responder.
          <Button variant="ghost" onClick={() => void transition("reopen")}>
            Reabrir
          </Button>
        </div>
      ) : (
        <form className="cx-msg-compose" onSubmit={send}>
          {suggestion && (
            <section className="cx-msg-ai-result" aria-label="Resultado do CosmosAI para revisão">
              <header>
                <h3>
                  <span className="cx-msg-ai-spark" aria-hidden="true">
                    <CosmosAiIcon size={14} />
                  </span>
                  {suggestion.kind === "improve" ? "Revisão do seu texto" : "Sugestão de resposta"}
                  <small>Demonstração local</small>
                </h3>
                <IconButton
                  label="Fechar resultado"
                  icon={<X aria-hidden="true" />}
                  variant="bare"
                  size="compact"
                  onClick={() => setSuggestion(null)}
                />
              </header>
              <p>{suggestion.text}</p>
              <div className="cx-msg-ai-actions">
                <Button
                  variant="primary"
                  size="compact"
                  onClick={() => {
                    setText(suggestion.text);
                    setSuggestion(null);
                  }}
                >
                  Usar no rascunho
                </Button>
                <Button variant="ghost" size="compact" onClick={() => setSuggestion(null)}>
                  Descartar
                </Button>
              </div>
            </section>
          )}
          {replyTo && (
            <div className="cx-msg-quote is-draft-reply">
              <button type="button" className="cx-msg-quote-body" onClick={() => jumpTo(replyTo)}>
                <strong>
                  Respondendo a{" "}
                  {byId.has(replyTo)
                    ? messageAuthor(byId.get(replyTo)!.direction, identity.name, counterpart)
                    : "mensagem indisponível"}
                </strong>
                <span>{messageExcerpt(byId.get(replyTo))}</span>
              </button>
              <IconButton
                label="Cancelar resposta"
                icon={<X aria-hidden="true" />}
                variant="bare"
                size="compact"
                onClick={() => setReplyTo(null)}
              />
            </div>
          )}
          {pending.length > 0 && (
            <ul className="cx-msg-attach-draft" aria-label="Anexos desta mensagem">
              {pending.map((item) => (
                <li key={item.id}>
                  <Paperclip size={13} aria-hidden="true" />
                  <span className="cx-msg-attach-name">{item.filename}</span>
                  <span className="cx-msg-attach-size cx-data">{fileSize(item.size)}</span>
                  <IconButton
                    label={`Remover ${item.filename}`}
                    icon={<X aria-hidden="true" />}
                    variant="bare"
                    size="compact"
                    onClick={() => setPending((current) => current.filter((f) => f.id !== item.id))}
                  />
                </li>
              ))}
            </ul>
          )}
          <div className="cx-msg-compose-row">
            <input
              ref={fileRef}
              type="file"
              multiple
              hidden
              accept={ATTACHMENT_ACCEPT[pickKindRef.current]}
              aria-hidden="true"
              tabIndex={-1}
              onChange={(event) => {
                addFiles(pickKindRef.current, event.target.files);
                event.target.value = "";
              }}
            />
            <Menu
              label="Adicionar anexo"
              align="start"
              sections={[
                {
                  id: "anexo",
                  items: [
                    {
                      id: "visual",
                      label: "Fotos e vídeos",
                      icon: <ImageIcon aria-hidden="true" />,
                      tone: "var(--accent)",
                    },
                    {
                      id: "document",
                      label: "Documento",
                      icon: <FileText aria-hidden="true" />,
                      tone: "var(--accent-2)",
                    },
                    {
                      id: "audio",
                      label: "Áudio",
                      icon: <Headphones aria-hidden="true" />,
                      tone: "var(--warn)",
                    },
                  ],
                },
              ]}
              onSelect={(id) => {
                pickKindRef.current = id as AttachmentKind;
                // O `accept` acompanha a categoria escolhida antes de abrir o
                // seletor do sistema; o React só o atualizaria no próximo render.
                if (fileRef.current) {
                  fileRef.current.accept = ATTACHMENT_ACCEPT[id as AttachmentKind];
                  fileRef.current.click();
                }
              }}
              trigger={(args) => (
                <IconButton
                  {...args}
                  label="Anexar arquivo"
                  icon={<Paperclip aria-hidden="true" />}
                  variant="bare"
                />
              )}
            />
            <IconButton
              label="Modelos de mensagem"
              icon={<FileText aria-hidden="true" />}
              variant="bare"
              onClick={() => setTemplatesOpen(true)}
            />
            <Menu
              label="CosmosAI"
              align="start"
              className="cx-msg-ai-menu"
              sections={[
                {
                  id: "ai",
                  items: [
                    {
                      id: "improve",
                      label: "Melhorar meu texto",
                      icon: <PenLine aria-hidden="true" />,
                      disabled: !text.trim(),
                      disabledReason: "Escreva uma mensagem para melhorar seu texto",
                    },
                    {
                      id: "suggest",
                      label: "Sugerir resposta",
                      icon: <CosmosAiIcon size={15} />,
                    },
                  ],
                },
              ]}
              onSelect={(id) =>
                setSuggestion(
                  id === "improve"
                    ? { kind: "improve", text: improveDraft(text) }
                    : { kind: "suggest", text: localSuggestion(lead?.name, items.at(-1)?.bodyText) },
                )
              }
              trigger={(args) => (
                <IconButton
                  {...args}
                  label="CosmosAI"
                  icon={<CosmosAiIcon size={16} />}
                  variant="bare"
                  className="cx-msg-ai"
                />
              )}
            />
            <textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Escreva uma mensagem…"
              aria-label="Mensagem"
              rows={1}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  void send(event);
                }
              }}
            />
            <IconButton
              label="Gravar áudio"
              icon={<Mic aria-hidden="true" />}
              variant="bare"
              disabled
              disabledReason="Gravar áudio depende de conexão real com o canal."
            />
            {(text.trim() || pending.length > 0) && (
              <button type="submit" className="cx-msg-send" disabled={busy} aria-label="Enviar mensagem">
                <Send aria-hidden="true" />
              </button>
            )}
          </div>
        </form>
      )}

      <TemplatesDialog
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        onInsert={(body) => {
          setText((current) => (current.trim() ? `${current.trimEnd()}\n\n${body}` : body));
          setTemplatesOpen(false);
        }}
        leadName={lead?.name}
        company={lead?.company}
      />
    </section>
  );
}

function channelName(channel: CommunicationChannel): string {
  return channel === "email" ? "E-mail" : CHAT_CHANNEL_LABEL[channel];
}

/** Biblioteca de modelos: insere o texto no rascunho, com as variáveis resolvidas. */
function TemplatesDialog({
  open,
  onClose,
  onInsert,
  leadName,
  company,
}: {
  open: boolean;
  onClose: () => void;
  onInsert: (body: string) => void;
  leadName?: string;
  company?: string;
}) {
  const store = useStore();
  const templates = (store.messageTemplates ?? []).filter((template) => !template.archived);
  const versions = store.messageTemplateVersions ?? [];
  const [query, setQuery] = useState("");

  const resolve = (text: string) =>
    text
      .replace(/\{\{\s*nome\s*\}\}/gi, leadName ?? "")
      .replace(/\{\{\s*empresa\s*\}\}/gi, company ?? "");

  const rows = templates
    .map((template) => {
      const published = versions
        .filter((version) => version.templateId === template.id && version.status === "published")
        .sort((left, right) => right.version - left.version)[0];
      return { template, published };
    })
    .filter((row) => Boolean(row.published))
    .filter((row) => row.template.name.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <Dialog open={open} onClose={onClose} title="Modelos de mensagem" description="Escolha uma resposta para reutilizar. As variáveis são preenchidas com os dados do cadastro.">
      <SearchInput value={query} onChange={setQuery} label="Buscar modelos" placeholder="Buscar por nome…" />
      <ul className="cx-msg-templates">
        {rows.length === 0 && <li className="cx-msg-template-empty">Nenhum modelo publicado.</li>}
        {rows.map(({ template, published }) => (
          <li key={template.id}>
            <button type="button" onClick={() => onInsert(resolve(published!.bodyText))}>
              <strong>{template.name}</strong>
              <span>{resolve(published!.bodyText).slice(0, 120)}</span>
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

/** Abre a conversa a partir do cadastro, como o "Nova conversa" do protótipo. */
function NewConversationButton() {
  const store = useStore();
  const [, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [channel, setChannel] = useState<"whatsapp" | "instagram" | "messenger">("whatsapp");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const leads = store.leads.filter((lead) => !lead.archived);

  const start = async () => {
    if (!leadId || !text.trim() || busy) return;
    setBusy(true);
    try {
      const result = await store.sendChannelMessage({ leadId, channel, bodyText: text.trim() });
      if (result.ok) {
        setOpen(false);
        setText("");
        setParams(
          (current) => {
            const next = new URLSearchParams(current);
            next.set("canal", channel);
            next.set("conversa", result.value.thread.id);
            return next;
          },
          { replace: true },
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="primary" icon={<Plus aria-hidden="true" />} onClick={() => setOpen(true)}>
        Nova conversa
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Nova conversa"
        description="A conversa nasce da primeira mensagem, no canal em que o cadastro tem endereço."
        dirty={Boolean(text.trim())}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" loading={busy} onClick={() => void start()} disabled={!leadId || !text.trim()}>
              Enviar primeira mensagem
            </Button>
          </>
        }
      >
        <Field label="Cadastro" required>
          {({ id }) => (
            <Select
              id={id}
              size="field"
              label="Cadastro"
              value={leadId}
              placeholder="Escolher cadastro"
              options={leads.map((lead) => ({ value: lead.id, label: `${lead.name} · ${lead.company}` }))}
              onChange={setLeadId}
            />
          )}
        </Field>
        <Field label="Canal" required>
          {({ id }) => (
            <Select
              id={id}
              size="field"
              label="Canal"
              value={channel}
              options={[
                { value: "whatsapp", label: "WhatsApp" },
                { value: "instagram", label: "Instagram" },
                { value: "messenger", label: "Messenger" },
              ]}
              onChange={(value) => setChannel(value as "whatsapp" | "instagram" | "messenger")}
            />
          )}
        </Field>
        <Field label="Mensagem" required>
          {({ id, describedBy }) => (
            <textarea
              id={id}
              aria-describedby={describedBy}
              className="cx-msg-new-text"
              value={text}
              rows={4}
              onChange={(event) => setText(event.target.value)}
            />
          )}
        </Field>
      </Dialog>
    </>
  );
}

/** Painel de contexto: quem é, em que etapa está e o que vem depois. */
function ContextPanel({
  thread,
  onClose,
  onOpenLead,
}: {
  thread: CommunicationThread;
  onClose: () => void;
  onOpenLead: (id: string) => void;
}) {
  const store = useStore();
  const lead = store.leads.find((item) => item.id === thread.leadId);
  const membership = store.memberships.find((item) => item.leadId === thread.leadId);
  const stages = store.stages
    .filter((stage) => stage.funnelId === membership?.funnelId && !stage.archived)
    .sort((left, right) => left.order - right.order);
  const funnel = store.funnels.find((item) => item.id === membership?.funnelId);
  const tasks = store.tasks.filter((task) => task.leadId === thread.leadId && task.status === "pending");
  const tags = store.tags.filter((tag) => lead?.tagIds.includes(tag.id));
  const now = new Date().toISOString();
  const funnelTopValue = Math.max(
    0,
    ...store.leads
      .filter((item) => store.memberships.some((m) => m.leadId === item.id && m.funnelId === membership?.funnelId))
      .map((item) => item.potentialValue || 0),
  );
  const heat = lead ? leadHeat({ lead, membership, stages, funnelTopValue, now }) : 0;
  const currentIndex = stages.findIndex((stage) => stage.id === membership?.stageId);
  const [taskOpen, setTaskOpen] = useState(false);
  /**
   * Abertura das seções, como no protótipo: jornada fechada e tarefas abertas.
   * É preferência de leitura da sessão, não dado do cadastro — por isso vive
   * aqui e não vira preferência persistida.
   */
  const [open, setOpen] = useState({ journey: false, tasks: true });
  const currentStage = stages[currentIndex];
  const priority = lead ? priorityLabel(lead.priority) : "";
  const priorityTone =
    lead?.priority === "high" ? "var(--crit)" : lead?.priority === "medium" ? "var(--warn)" : "var(--muted)";
  // O canal da conversa decide qual endereço o painel mostra: é o endereço por
  // onde esta conversa acontece, não uma lista de contatos.
  const channelFact =
    thread.channel === "email"
      ? { label: "E-mail", value: lead?.email }
      : thread.channel === "instagram"
        ? { label: "Instagram", value: lead?.instagram }
        : { label: "Telefone", value: lead?.phone };

  if (!lead)
    return (
      <aside className="cx-msg-context" aria-label="Sobre o contato">
        <h2 className="cx-msg-section-title">
          Sobre o contato
          <IconButton label="Fechar detalhes" icon={<X aria-hidden="true" />} variant="bare" onClick={onClose} />
        </h2>
        <p className="cx-msg-context-empty">
          Esta conversa ainda não está vinculada a um cadastro. Vincule para ver a jornada e as tarefas.
        </p>
      </aside>
    );

  return (
    <aside className="cx-msg-context" aria-label="Sobre o contato">
      <h2 className="cx-msg-section-title">
        Sobre o contato
        <IconButton label="Fechar detalhes" icon={<X aria-hidden="true" />} variant="bare" onClick={onClose} />
      </h2>
      <div className="cx-msg-identity">
        <ChannelAvatar name={lead.name} channel={thread.channel} size="lg" />
        <p className="cx-msg-context-name">{lead.name}</p>
        <p className="cx-msg-context-company">
          {lead.company}
          <br />
          {lead.role}
        </p>
      </div>
      {/* Etapa e prioridade vêm antes das tags: é o que situa a conversa. */}
      <div className="cx-msg-tags">
        {currentStage && (
          <span style={{ "--cx-msg-tone": paletteColor(currentStage.color) } as CSSProperties}>{currentStage.name}</span>
        )}
        <span style={{ "--cx-msg-tone": priorityTone } as CSSProperties}>
          Prioridade {priority.toLowerCase()}
        </span>
        {tags.map((tag) => (
          <span key={tag.id} style={{ "--cx-msg-tone": paletteColor(tag.color) } as CSSProperties}>
            {tag.name}
          </span>
        ))}
      </div>
      <div className="cx-msg-context-actions">
        <Button variant="primary" icon={<User aria-hidden="true" />} onClick={() => onOpenLead(lead.id)}>
          Perfil
        </Button>
        <Button variant="primary" icon={<Plus aria-hidden="true" />} onClick={() => setTaskOpen(true)}>
          Tarefa
        </Button>
      </div>
      {taskOpen && (
        <NewTaskDialog open progress="todo" fixedLeadId={lead.id} onClose={() => setTaskOpen(false)} />
      )}
      <div className="cx-msg-context-section">
        <dl className="cx-msg-facts">
          <div>
            <dt>{channelFact.label}</dt>
            <dd>{channelFact.value || "Não informado"}</dd>
          </div>
          <div>
            <dt>Cidade</dt>
            <dd>{lead.city ? `${lead.city}${lead.state ? ` · ${lead.state}` : ""}` : "Não informada"}</dd>
          </div>
          <div>
            <dt>Origem</dt>
            <dd className="is-accent">{lead.source}</dd>
          </div>
          <div>
            <dt>
              {lead.clientSince ? "Valor do negócio" : "Valor potencial"}
              <HelpPopover
                about="o valor"
                label="Sobre o valor"
                align="end"
                items={VALUE_EXPLANATION}
              />
            </dt>
            <dd className="cx-msg-context-value num">{money(lead.potentialValue || 0)}</dd>
          </div>
        </dl>
        <div className="cx-msg-score">
          <span className="cx-msg-score-label">
            AI Score
            <HelpPopover about="o AI Score" label="O que é o AI Score" align="end" items={HEAT_EXPLANATION} />
          </span>
          <span className="cx-msg-score-value">
            <strong className="num">{heat}</strong>
            <span className="cx-msg-score-meter" aria-hidden="true">
              <i style={{ width: `${heat}%` }} />
            </span>
          </span>
        </div>
      </div>
      {stages.length > 0 && (
        <details
          className="cx-msg-context-section cx-msg-disclosure"
          open={open.journey}
          onToggle={(event) => {
            // O valor é lido agora: dentro do atualizador o React já zerou
            // `currentTarget`, e o acesso tardio derrubava a página inteira.
            const aberto = event.currentTarget.open;
            setOpen((current) => ({ ...current, journey: aberto }));
          }}
        >
          <summary>
            <ChevronDown aria-hidden="true" />
            <span>Jornada no funil</span>
            <small>
              {currentIndex + 1} de {stages.length}
            </small>
          </summary>
          <div className="cx-msg-disclosure-body">
            <p className="cx-msg-funnel-name">{funnel?.name}</p>
            <ol className="cx-msg-journey">
              {stages.map((stage, index) => (
                <li
                  key={stage.id}
                  className={index < currentIndex ? "is-past" : index === currentIndex ? "is-current" : undefined}
                  style={{ "--cx-msg-tone": paletteColor(stage.color) } as CSSProperties}
                >
                  <span className="cx-msg-step">{index + 1}</span>
                  <span className="cx-msg-step-name">{stage.name}</span>
                  {index === currentIndex && <small>Atual</small>}
                </li>
              ))}
            </ol>
          </div>
        </details>
      )}
      <details
        className="cx-msg-context-section cx-msg-disclosure"
        open={open.tasks}
        onToggle={(event) => {
          const aberto = event.currentTarget.open;
          setOpen((current) => ({ ...current, tasks: aberto }));
        }}
      >
        <summary>
          <ChevronDown aria-hidden="true" />
          <span>Próximas tarefas</span>
          <small>{tasks.length}</small>
        </summary>
        <div className="cx-msg-disclosure-body cx-msg-task-list">
          {tasks.length === 0 && <p className="cx-msg-context-empty">Nenhuma tarefa pendente.</p>}
          {tasks.map((task) => {
            const overdue = task.dueAt < now;
            return (
              <button
                key={task.id}
                type="button"
                className="cx-msg-task"
                onClick={() => onOpenLead(lead.id)}
              >
                <span className="cx-msg-task-title">{task.description}</span>
                <small className={overdue ? "is-overdue" : undefined}>
                  <Clock aria-hidden="true" />
                  {new Date(task.dueAt).toLocaleDateString("pt-BR")} ·{" "}
                  {new Date(task.dueAt).toLocaleTimeString("pt-BR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </small>
              </button>
            );
          })}
        </div>
      </details>
    </aside>
  );
}
